<template>
  <div class="home">
    <div class="container is-fluid">
      <page-header :entity="$entitiesName.OrderItem">
        <h1 class="title">
          {{ $t("orderItems.title") }}
        </h1>
      </page-header>

      <div class="level">
        <div class="level-left"></div>
        <div class="level-right">
          <div class="level-item">
            <date-field
              class="no-margin"
              :label="$t('orderItems.filters.tokenStartDate')"
              v-model="startDate"
              :inline="true"
              :edit="true"
            />
            <date-field
              class="no-margin"
              :label="$t('orderItems.filters.tokenEndDate')"
              v-model="endDate"
              :inline="true"
              :edit="true"
            />
          </div>
        </div>
      </div>

      <datatable
        :fetch="$api.fetchOrderItems"
        ref="orderItemsList"
        :enableDownload="true"
        objectName="budget/order-items"
        :externalFilters="{
          startDate: startDate,
          endDate: endDate,
        }"
      >
        <div
          slot="agencyName"
           slot-scope="{ item }"
          :title="$t('orderItems.columns.agencyName')"
          nofilter
        >
        <restricted-link
            v-if="item"
            entity="agency"
            :to="`/sst/agency/${item.agencyId}`"
          >
            {{ item.agencyName }}
          </restricted-link>
        </div>

        <div
          slot="sapReference"
          slot-scope="{ item }"
          :title="$t('orderItems.columns.sapReference')"
        >
          {{ item.sapReference }}
        </div>

        <div
          slot="order"
          slot-scope="{ item: { order } }"
          :title="$t('orderItems.columns.orderReference')"
        >
          <restricted-link
            v-if="order"
            entity="order"
            :to="`/sst/order/${order.id}`"
          >
            {{ order.reference }}
          </restricted-link>
        </div>

        <div
          slot="attachement"
          slot-scope="{ item: { attachement } }"
          :title="$t('orderItems.columns.attachementReference')"
        >
          <restricted-link
            v-if="attachement"
            entity="attachement"
            :to="`/sst/attachement/${attachement.id}`"
          >
            {{ attachement.reference }}
          </restricted-link>
        </div>

        <div
          slot="code"
          slot-scope="{ item }"
          :title="$t('orderItems.columns.status')"
          advancedFilter
          :advancedFilterOptions="statusFilterOptions"
          nofilter
        >
          <span v-if="item.code">{{ $t("attachementStatus." + item.code) }}</span>
        </div>

        <div
          slot="tokenCreatedAt"
          slot-scope="{ item }"
          :title="$t('orderItems.columns.tokenCreatedAt')"
          nofilter
        >
          <span v-if="item.tokenCreatedAt">{{
            item.tokenCreatedAt | reverseDate
          }}</span>
        </div>

        <div
          slot="subcontractorMarket"
          slot-scope="{ item: { subcontractorMarket } }"
          :title="$t('orderItems.columns.subcontractorMarket')"
        >
          <restricted-link
            v-if="subcontractorMarket"
            entity="subcontractor_market"
            :to="`/sst/market/${subcontractorMarket.id}`"
          >
            {{ subcontractorMarket.edifice }}
          </restricted-link>
        </div>

        <div
          slot="siret"
          slot-scope="{ item }"
          :title="$t('orderItems.columns.siret')"
        >
          {{ item.siret }}
        </div>

        <div
          slot="project"
          slot-scope="{ item: { project } }"
          :title="$t('orderItems.columns.project')"
        >
          <restricted-link
            v-if="project"
            entity="project"
            :to="`/project/${project.id}`"
          >
            {{ project.name }}
          </restricted-link>
        </div>

        <div
          slot="oracleProject"
          slot-scope="{ item: { oracleProject } }"
          :title="$t('orderItems.columns.oracleProject')"
        >
          {{ oracleProject && oracleProject.name }}
        </div>

        <div
          slot="oracleTask"
          slot-scope="{ item: { oracleTask } }"
          :title="$t('orderItems.columns.oracleTask')"
        >
          {{ oracleTask && oracleTask.name }}
        </div>

        <div
          slot="prestationCode"
          slot-scope="{ item: { prestation } }"
          :title="$t('orderItems.columns.prestationCode')"
        >
          <span v-if="prestation">{{ prestation.code }}</span>
        </div>

        <div
          slot="designation"
          slot-scope="{ item: { prestation } }"
          :title="$t('orderItems.columns.designation')"
        >
          <span v-if="prestation">{{ prestation.description }}</span>
        </div>

        <div
          slot="unit"
          slot-scope="{ item: { unit } }"
          :title="$t('orderItems.columns.unit')"
        >
          <span v-if="unit">{{ unit.name }}</span>
        </div>

        <div
          slot="unitPrice"
          slot-scope="{ item: { prestationSST } }"
          :title="$t('orderItems.columns.unitPrice')"
          class="has-text-right"
          nofilter
        >
          <span v-if="prestationSST">{{ prestationSST.cost | priceEUR }}</span>
        </div>

        <div
          slot="prestationQuantity"
          slot-scope="{ item }"
          :title="$t('orderItems.columns.prestationQuantity')"
          class="has-text-right"
          nofilter
        >
          {{ item.prestationQuantity }}
        </div>

        <div
          slot="orderedAmount"
          slot-scope="{ item }"
          :title="$t('orderItems.columns.orderedAmount')"
          class="has-text-right"
          nofilter
        >
          <span v-if="item.prestationSST">
            {{ (item.prestationSST.cost * item.prestationQuantity) | priceEUR }}
          </span>
        </div>
      </datatable>
    </div>
  </div>
</template>

<script>
import { Workflows } from "@/constants/Workflow";

export default {
  name: "order-items-list",
  data() {
    return {
      startDate: "",
      endDate: "",
    };
  },
  computed: {
    statusFilterOptions() {
      return [
        { label: this.$t("common.noFilter"), value: "" },
        ...Object.values(Workflows.ATT.NODE)
          .filter((code) => code !== Workflows.ATT.NODE.OBJECT_NAME)
          .map((code) => ({
            label: this.$t(`attachementStatus.${code}`),
            value: code,
          })),
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.no-margin:not(:last-child) {
  margin-bottom: 0;
}

::v-deep .table {
  th:nth-child(3),
  td[cy-data="order"] {
    min-width: 215px;
    white-space: normal;
  }

  th:nth-child(11),
  td[cy-data="designation"] {
    min-width: 280px;
    white-space: normal;
  }
}
</style>
